/* eslint-disable react/no-unknown-property */
import { Html } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import React, { useMemo, useRef, useState } from 'react'
import type { ComponentProps } from 'react'
import * as THREE from 'three'

type HtmlProps = ComponentProps<typeof Html>

export interface MarkerInterface extends Omit<
  HtmlProps,
  'occlude' | 'sprite' | 'distanceFactor'
> {
  distanceFactor?: number
  minScale?: number
  maxScale?: number
  occlude?: HtmlProps['occlude']
  transform?: boolean
  visible?: boolean
  anchor?: 'bottom' | 'center'
}

const ANCHOR_OFFSET: Record<NonNullable<MarkerInterface['anchor']>, string> = {
  bottom: 'translate(-50%, -100%)',
  center: 'translate(-50%, -50%)',
}

const ANCHOR_ORIGIN: Record<NonNullable<MarkerInterface['anchor']>, string> = {
  bottom: 'bottom center',
  center: 'center',
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

function Marker({
  children,
  distanceFactor,
  minScale = 0.3,
  maxScale = 1.5,
  occlude = true,
  transform = false,
  visible = true,
  anchor = 'center',
  style,
  ...props
}: MarkerInterface) {
  const ref = useRef<THREE.Group>(null)
  const scaleRef = useRef<HTMLDivElement>(null)
  const referenceDistance = useRef<number>()

  const [isOccluded, setOccluded] = useState(false)
  const isVisible = visible && !isOccluded

  const vec = useMemo(() => new THREE.Vector3(), [])

  const handleElementScale = (camera: THREE.Camera) => {
    if (!ref.current) return

    const distance = camera.position.distanceTo(
      ref.current.getWorldPosition(vec)
    )

    if (transform || !scaleRef.current) return

    if (referenceDistance.current === undefined) {
      referenceDistance.current = distance
    }

    const isOrthographic = (camera as THREE.OrthographicCamera)
      .isOrthographicCamera
    const factor = distanceFactor ?? referenceDistance.current
    const scale = isOrthographic
      ? 1
      : clamp(factor / Math.max(distance, 1e-6), minScale, maxScale)

    scaleRef.current.style.transform = `scale(${scale})`
  }

  useFrame(({ camera }) => {
    if (!ref.current) return
    handleElementScale(camera)
  })

  return (
    <group ref={ref}>
      <Html
        center={transform}
        transform={transform}
        sprite={transform}
        distanceFactor={transform ? distanceFactor : undefined}
        occlude={occlude}
        onOcclude={setOccluded}
        zIndexRange={[100, 0]}
        style={{
          transition: 'opacity 0.2s',
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? 'auto' : 'none',
          ...style,
        }}
        {...props}
      >
        <div
          style={{
            display: 'inline-block',
            transform: transform ? undefined : ANCHOR_OFFSET[anchor],
          }}
        >
          <div
            ref={scaleRef}
            style={{ transformOrigin: ANCHOR_ORIGIN[anchor] }}
          >
            {children}
          </div>
        </div>
      </Html>
    </group>
  )
}

export default Marker
